import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Save, Trash2, Eye, Phone, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";

function TemplateBar({ subject, body, onPick }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState("");
  const load = useCallback(() => { api.get("/admin/email-templates").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const pick = (id) => { setSel(id); const t = list.find((x) => x.id === id); if (t) onPick(t); };
  const save = async () => {
    const cur = list.find((x) => x.id === sel);
    const name = window.prompt("Template name", cur?.name || subject);
    if (!name?.trim()) return;
    try { const { data } = await api.post("/admin/email-templates", { name, subject, body }); toast.success(data.updated ? "Template updated" : "Template saved"); setSel(data.id); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    const t = list.find((x) => x.id === sel);
    if (!t || !window.confirm(`Delete the template “${t.name}”?`)) return;
    try { await api.delete(`/admin/email-templates/${t.id}`); toast.success("Template deleted"); setSel(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="template-bar">
      <select value={sel} onChange={(e) => pick(e.target.value)} className="flex-1 min-w-[160px] h-9 rounded-xl border border-slate-200 px-2 text-sm bg-white" data-testid="template-select">
        <option value="">Start from a template…</option>
        {list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={save} disabled={!subject.trim() || !body.trim()} data-testid="template-save"><Save className="w-3.5 h-3.5 mr-1" /> Save as template</Button>
      {sel && <Button type="button" size="sm" variant="ghost" className="rounded-full text-red-600" onClick={del} data-testid="template-delete"><Trash2 className="w-3.5 h-3.5" /></Button>}
    </div>
  );
}

export function ComposeSchoolEmail({ school, count, onlyPass, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [inApp, setInApp] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (school) { setSubject(""); setBody(""); } }, [school]);
  const send = async () => {
    if (!window.confirm(`Send this email to ${count} famil${count === 1 ? "y" : "ies"} at ${school}? Each parent gets their own private copy.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/school-email", { school, subject, body, only_unused_pass: onlyPass, in_app: inApp }); toast.success(`Sending to ${data.recipients} families — check progress below`); onClose(true); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!school} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-lg" data-testid="compose-school-email">
        <DialogHeader><DialogTitle className="font-display text-navy">Email {school}</DialogTitle>
          <DialogDescription>{count} famil{count === 1 ? "y" : "ies"}{onlyPass ? " with an unused Welcome Pass" : ""}. Each parent receives a separate, private email starting “Hi [name],”.</DialogDescription></DialogHeader>
        {school && <TemplateBar subject={subject} body={body} onPick={(t) => { setSubject(t.subject); setBody(t.body); }} />}
        <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl" data-testid="compose-subject" />
        <Textarea placeholder="Your message… (leave a blank line between paragraphs)" value={body} onChange={(e) => setBody(e.target.value)} className="rounded-xl min-h-[180px]" data-testid="compose-body" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={inApp} onChange={(e) => setInApp(e.target.checked)} data-testid="compose-in-app" /> Also post it as an in-app message</label>
        <Button onClick={send} disabled={busy || !subject.trim() || !body.trim()} className="w-full rounded-full bg-turquoise text-navy font-bold h-11" data-testid="compose-send"><Send className="w-4 h-4 mr-1" /> Send to {count}</Button>
      </DialogContent>
    </Dialog>
  );
}

function OpensList({ cid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/admin/school-email/${cid}/opens`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [cid]);
  if (!rows) return <p className="text-xs text-slate-400 pl-3">Loading…</p>;
  const notOpened = rows.filter((r) => !r.opened_at && !r.failed);
  return (
    <div className="pl-3 py-1 space-y-0.5" data-testid={`opens-list-${cid}`}>
      {notOpened.length > 0 && <p className="text-[11px] font-bold text-slate-500">Not opened yet — may need a follow-up call ({notOpened.length})</p>}
      {rows.map((r) => (
        <p key={r.email} className="text-xs flex flex-wrap gap-x-2" data-testid={`open-row-${r.email}`}>
          <span className={r.failed ? "text-red-600 font-bold" : r.opened_at ? "text-turquoise font-bold" : "text-slate-700 font-bold"}>{r.failed ? "Failed to send" : r.opened_at ? "✓ Opened" : "Not opened"}</span>
          <span className="text-navy">{r.parent_name || r.email}</span>
          <span className="text-slate-400">{r.email}</span>
          {r.phone && !r.opened_at && <a href={`tel:${r.phone}`} className="text-navy underline inline-flex items-center gap-0.5"><Phone className="w-3 h-3" />{r.phone}</a>}
          {r.opened_at && <span className="text-slate-400">{new Date(r.opened_at).toLocaleString()}</span>}
        </p>
      ))}
    </div>
  );
}

export function EmailHistory({ refresh }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { api.get("/admin/school-email/history").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refresh]);
  const resend = async (r) => {
    const n = r.recipients - (r.opened || 0);
    if (!window.confirm(`Resend “${r.subject}” to the ${n} parent${n === 1 ? "" : "s"} who haven't opened it?`)) return;
    try { const { data } = await api.post(`/admin/school-email/${r.id}/resend-unopened`); toast.success(`Resending to ${data.recipients} parents`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => {
    if (!rows.some((r) => r.status === "sending")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [rows, load]);
  if (!rows.length) return null;
  return (
    <div className="border-t border-slate-100 pt-2" data-testid="school-email-history">
      <p className="text-xs font-bold text-slate-500 mb-1">Sent emails <span className="font-normal text-slate-400">· opens are approximate: some email apps hide or pre-load images</span></p>
      {rows.map((r) => (
        <div key={r.id}>
          <div className="text-xs text-slate-600 py-0.5 flex flex-wrap items-center gap-x-1" data-testid={`campaign-${r.id}`}>
            <b className="text-navy">{r.subject}</b> → {r.school} · {r.sent}/{r.recipients} sent{r.failed?.length ? ` · ${r.failed.length} failed (${r.failed.join(", ")})` : ""} · {r.status === "sending" ? "sending…" : new Date(r.created_at).toLocaleString()}
            {r.tracked ? <button onClick={() => setOpen(open === r.id ? null : r.id)} className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-turquoise/15 text-navy font-bold px-2 py-0.5 hover:bg-turquoise/30 transition-colors" data-testid={`campaign-opens-${r.id}`}>
              <Eye className="w-3 h-3" /> {r.opened || 0}/{r.recipients} opened
            </button> : <span className="ml-1 text-slate-400">· opens not tracked</span>}
            {r.resend_of && <span className="text-[10px] bg-gold/30 text-navy rounded-full px-2">resend</span>}
            {r.tracked && r.status !== "sending" && (r.opened || 0) < r.recipients && (
              <button onClick={() => resend(r)} className="inline-flex items-center gap-0.5 rounded-full bg-navy text-white font-bold px-2 py-0.5 hover:bg-navy/80 transition-colors" data-testid={`campaign-resend-${r.id}`}>
                <RotateCw className="w-3 h-3" /> Resend to {r.recipients - (r.opened || 0)} unopened
              </button>)}
          </div>
          {open === r.id && <OpensList cid={r.id} />}
        </div>
      ))}
    </div>
  );
}
